import { useEffect, useState } from 'react';
import dashboardService from '../../../services/DashboardService.js';
import { hasShipmentFeatureAccess } from '../../../utils/networkMode.js';
import { fullCurrency } from '../../../utils/formatCurrency.js';
import ReturnBreakdownModal from './ReturnBreakdownModal.js';

const money = (value) => fullCurrency(value || 0);
const sumRows = (rows, key) => (rows || []).reduce((total, row) => total + Number(row[key] || 0), 0);
const CREDIT_ROLES = ['Admin', 'Accountant', 'System Admin'];

const CreditClientsSection = ({
  user,
  dateRange = 'all_time',
  customStart = '',
  customEnd = '',
}) => {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openClientId, setOpenClientId] = useState(null);
  const hidden = !user || !CREDIT_ROLES.includes(user.role) || !hasShipmentFeatureAccess(user);

  useEffect(() => {
    if (hidden) return undefined;
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = { date_range: dateRange || 'all_time' };
    if (dateRange === 'custom') {
      params.from_date = customStart;
      params.to_date = customEnd;
    }
    dashboardService.getCreditClients(params)
      .then((res) => {
        if (!cancelled) setRows(res?.data?.credit_clients || []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [hidden, user?.user_id, user?.company_id, dateRange, customStart, customEnd]);

  if (hidden) return null;
  if (!loading && Array.isArray(rows) && rows.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm mb-6 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100">
        <h2 className="text-sm font-semibold text-gray-800">Credit clients</h2>
      </div>
      {loading && !rows ? (
        <p className="px-4 py-6 text-sm text-gray-400">Loading credit clients…</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-3 py-2">Client</th>
                <th className="text-left px-3 py-2">Company</th>
                <th className="text-right px-3 py-2">Billed</th>
                <th className="text-right px-3 py-2">Recovered</th>
                <th className="text-right px-3 py-2">Outstanding</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(rows || []).map((row) => (
                <tr
                  key={row.client_id}
                  onClick={() => setOpenClientId(row.client_id)}
                  className="cursor-pointer hover:bg-sky-50/70"
                >
                  <td className="px-3 py-2 font-medium text-gray-900">{row.name}</td>
                  <td className="px-3 py-2 text-gray-600">{row.company_name || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(row.billed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(row.recovered)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{money(row.outstanding)}</td>
                </tr>
              ))}
            </tbody>
            {(rows || []).length ? (
              <tfoot>
                <tr className="bg-gray-50 font-semibold text-gray-900">
                  <td className="px-3 py-2" colSpan={2}>Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'billed'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-emerald-800">{money(sumRows(rows, 'recovered'))}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(sumRows(rows, 'outstanding'))}</td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
      {openClientId ? (
        <ReturnBreakdownModal
          kind="client"
          clientId={openClientId}
          dateRange={dateRange}
          customStart={customStart}
          customEnd={customEnd}
          onClose={() => setOpenClientId(null)}
        />
      ) : null}
    </div>
  );
};

export default CreditClientsSection;
